import { useEffect } from "react";
import { X } from "lucide-react";

const Pill = ({ tone, children }) => {
  const map = {
    important: "bg-coral/15 text-coralDeep border-coral/30",
    watch: "bg-ink/8 text-mutedink border-ink/15",
    good: "bg-sage/30 text-[#4d6b3f] border-sage",
  };
  return (
    <span className={`flex-none text-[0.6rem] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full border ${map[tone]}`}>
      {children}
    </span>
  );
};

const Tag = ({ tone, children }) => {
  const map = {
    keep: "bg-sage/30 text-[#4d6b3f]",
    stop: "bg-coral/20 text-coralDeep",
    add: "bg-ink/10 text-ink",
  };
  return <span className={`flex-none text-[0.58rem] font-bold uppercase tracking-wide px-2 py-1 rounded ${map[tone]}`}>{children}</span>;
};

const Kicker = ({ n, children }) => (
  <div className="flex items-center gap-3 mb-4">
    <span className="font-disp font-extrabold text-2xl text-coral leading-none">{n}</span>
    <span className="font-disp font-bold text-xl text-ink">{children}</span>
  </div>
);

const Section = ({ children }) => <div className="px-6 md:px-10 py-8 border-b border-ink/10">{children}</div>;

const READS = [
  ["Pores & congestion", "Oil meeting dead skin where pores clog. This is your focus.", "important"],
  ["Hydration", "Reading low. The midday tightness is dehydration, not dryness.", "important"],
  ["Sun sensitivity", "Tropical UV with no daily SPF is what keeps the marks dark.", "important"],
  ["Oil & shine", "Genuine oil up top, but not the whole story here.", "watch"],
  ["Breakout activity", "Active in the T-zone, settling elsewhere.", "watch"],
  ["Tone & marks", "Old marks, fading slowly, fully reversible.", "watch"],
];

export default function PassportModal({ open, onClose }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-ink/70 backdrop-blur-sm flex items-start justify-center p-3 sm:p-6 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-lenis-prevent
      data-testid="passport-modal"
    >
      <div className="relative w-full max-w-[760px] my-4 bg-cream rounded-[1.5rem] border-[2.5px] border-ink shadow-[10px_10px_0_rgba(61,43,36,0.3)] overflow-hidden">
        {/* Bar */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 md:px-10 py-4 bg-cream/95 backdrop-blur border-b border-ink/10">
          <span className="font-disp font-bold text-ink"><span className="text-coral">sae</span>bit · Skin Passport</span>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full border-2 border-ink flex items-center justify-center text-ink hover:bg-ink hover:text-cream transition-colors"
            aria-label="Close passport"
            data-testid="passport-modal-close"
          >
            <X size={18} strokeWidth={2.5} />
          </button>
        </div>

        {/* Hero */}
        <div className="px-6 md:px-10 py-9 bg-ink text-cream">
          <div className="flex justify-between text-[0.62rem] font-bold uppercase tracking-[0.16em] text-sage mb-5">
            <span>Skin Passport</span><span className="text-cream/55">#0042 · Member</span>
          </div>
          <div className="text-[0.7rem] font-bold uppercase tracking-[0.18em] text-coral mb-2">Your record</div>
          <div className="font-disp font-extrabold text-3xl mb-5">Your Skin Passport</div>
          <div className="grid sm:grid-cols-3 gap-4 mb-5">
            {[["Client", "Maya R. · 16"], ["First visit", "22 June 2026"], ["Location", "Kuala Lumpur"]].map(([k, v]) => (
              <div key={k}>
                <span className="block text-[0.6rem] font-bold uppercase tracking-[0.14em] text-cream/45 mb-1">{k}</span>
                <span className="text-sm text-cream/90">{v}</span>
              </div>
            ))}
          </div>
          <p className="font-accent italic text-cream/60">Measured today, written in plain words, yours to keep. Every visit adds a page.</p>
        </div>

        {/* 01 What you told us */}
        <Section>
          <Kicker n="01">What you told us</Kicker>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              "Shiny by midday, but tight right after I wash.",
              "Breakouts keep coming back in the same spots.",
              "Old marks stick around for months.",
              "I switch products the moment something is slow.",
              "I skip SPF most days.",
            ].map((q) => (
              <div key={q} className="font-accent italic text-ink/85 bg-paper border border-ink/10 rounded-xl px-4 py-3 text-[0.98rem]">
                “{q}”
              </div>
            ))}
          </div>
        </Section>

        {/* 02 What we measured */}
        <Section>
          <Kicker n="02">What we measured</Kicker>
          <p className="text-mutedink leading-relaxed mb-5">
            Seven readings, measured against where skin should sit at 16. The three flagged <strong className="text-ink font-semibold">Important</strong> are below that line, your targets this block. No raw scores; your coach reads each aloud.
          </p>
          <div className="space-y-2.5">
            {READS.map(([name, line, tone]) => (
              <div key={name} className="flex items-center justify-between gap-4 bg-paper border border-ink/10 rounded-xl px-4 py-3">
                <div>
                  <div className="font-semibold text-ink">{name}</div>
                  <div className="text-sm text-mutedink">{line}</div>
                </div>
                <Pill tone={tone}>{tone === "important" ? "Important" : "Watch"}</Pill>
              </div>
            ))}
            <div className="flex items-center justify-between gap-4 bg-sage/15 border border-sage rounded-xl px-4 py-3">
              <div>
                <div className="text-[0.6rem] font-bold uppercase tracking-wide text-[#4d6b3f]">Holding steady</div>
                <div className="font-semibold text-ink">Calm vs. redness</div>
              </div>
              <Pill tone="good">Doing well</Pill>
            </div>
          </div>
          <p className="text-sm italic text-mutedink mt-3">Barrier's intact, little reactivity. The job is to keep it that way.</p>
        </Section>

        {/* 03 Routine */}
        <Section>
          <Kicker n="03">What your routine tells us</Kicker>
          <div className="space-y-2.5">
            {[
              ["keep", "Gentle non-foaming cleanser", "isn't stripping your barrier."],
              ["stop", "Foaming wash & weekly switching", "are out; give an active six weeks."],
              ["add", "Daily broad-spectrum SPF", "is your biggest lever on the marks."],
              ["add", "One gentle BHA, 2–3× a week", "clears congestion without scrubbing."],
            ].map(([tone, bold, rest]) => (
              <div key={bold} className="flex gap-3 items-start bg-paper border border-ink/10 rounded-xl px-4 py-3">
                <Tag tone={tone}>{tone}</Tag>
                <p className="text-ink/90"><strong className="font-semibold">{bold}</strong> <span className="text-mutedink">{rest}</span></p>
              </div>
            ))}
          </div>
        </Section>

        {/* 04 Pattern + Identity */}
        <Section>
          <Kicker n="04">The pattern underneath</Kicker>
          <p className="text-mutedink leading-relaxed mb-6">
            Three readings line up: oil on top, low water underneath, pores caught between them. Stripping the oil taught your skin to make more. The marks aren't fresh damage; they're old breakouts the sun keeps darkening. <em className="font-accent italic text-ink">One thread runs through all of it.</em>
          </p>
          <div className="relative bg-ink text-cream rounded-2xl p-7 overflow-hidden">
            <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full bg-coral/30 blur-2xl" />
            <div className="relative">
              <div className="text-[0.7rem] font-bold uppercase tracking-[0.18em] text-sage mb-2">Your Identity</div>
              <div className="font-disp font-extrabold text-3xl mb-2">Pore Clarity</div>
              <div className="text-cream/80 mb-5 max-w-md">Clearing congestion by fixing the oil-and-water balance underneath it, not by scrubbing harder.</div>
              <div className="text-sm text-cream/70 space-y-1.5">
                <div>Identity <b className="text-cream">04</b>, your conditioning focus, read from assessment, scan and routine together.</div>
                <div className="text-cream/50">Saegyeol™ skin type: <b>달빛 Dalbich · Moonlit</b>, reads oily on the surface, runs dry underneath.</div>
              </div>
            </div>
          </div>
        </Section>

        {/* 05 First priority */}
        <Section>
          <Kicker n="05">Your first priority</Kicker>
          <div className="font-disp font-bold text-2xl text-ink leading-snug mb-3">Stop stripping. Let your skin rebalance, and the pores will clear.</div>
          <p className="text-mutedink leading-relaxed mb-5">
            For the next six weeks we protect water instead of stripping oil: a gentle cleanse, real hydration, daily SPF, and one steady active held long enough to work. That is the whole job right now. Everything else waits its turn.
          </p>
          <div className="text-[0.62rem] font-bold uppercase tracking-[0.16em] text-coralDeep mb-3">Your targets this block</div>
          <div className="flex flex-wrap gap-2.5">
            {[["Hydration", "→ into range for 16"], ["Congestion", "→ clearing"], ["Sun cover", "→ daily"]].map(([a, b]) => (
              <div key={a} className="border-[1.5px] border-ink rounded-full px-4 py-2 font-semibold text-sm text-ink">
                {a} <span className="text-coralDeep">{b}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* 06 Protocol */}
        <Section>
          <Kicker n="06">Your protocol</Kicker>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="text-[0.62rem] font-bold uppercase tracking-[0.16em] text-coralDeep mb-3">In the studio</div>
              <ol className="space-y-2.5">
                {[
                  "Deep cleanse, oil phase, then gentle gel.",
                  "Sono LDM, soothes the barrier and calms inflammation.",
                  "Gentle extraction, only what's ready.",
                  "Cryo cool-down, calms surface inflammation and acne redness.",
                  "Hydrating layer + SPF to finish.",
                ].map((s, i) => (
                  <li key={s} className="flex gap-3 text-ink/90">
                    <span className="flex-none w-6 h-6 rounded-full bg-clay text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                    {s}
                  </li>
                ))}
              </ol>
            </div>
            <div>
              <div className="text-[0.62rem] font-bold uppercase tracking-[0.16em] text-coralDeep mb-3">At home</div>
              <div className="space-y-3">
                {[
                  ["MORNING", "Gentle cleanse → hydrating essence → SPF."],
                  ["NIGHT", "Cleanse → BHA (2–3× / week) → moisturiser."],
                  ["THE RULE", "Hold it six weeks. No new actives."],
                ].map(([k, v]) => (
                  <div key={k} className="bg-paper border border-ink/10 rounded-xl px-4 py-3">
                    <b className="block text-[0.62rem] font-bold uppercase tracking-[0.14em] text-coral mb-1">{k}</b>
                    <p className="text-sm text-mutedink">{v}</p>
                  </div>
                ))}
                <div className="bg-sage/15 border border-sage rounded-xl px-4 py-3">
                  <b className="block text-[0.62rem] font-bold uppercase tracking-[0.14em] text-[#4d6b3f] mb-1">How it'll feel</b>
                  <p className="text-sm text-mutedink">A light purge around week two is normal, don't quit on it. By your next scan we want hydration up and congestion down.</p>
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* 07 Checkpoints */}
        <Section>
          <Kicker n="07">Your next checkpoint</Kicker>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              ["Today", "Baseline measured", "Passport opened. This is page one. Everything measures against today.", true],
              ["~4 weeks", "Re-scan & compare", "Hydration up, congestion easing, fewer fresh marks. The numbers guide the next move.", false],
              ["Long game", "Skin that holds", "Even tone, steady oil, pores that stay clear: conditioned, not patched.", false],
            ].map(([when, name, body, now]) => (
              <div key={when} className={`rounded-2xl p-5 border-2 ${now ? "bg-ink text-cream border-ink" : "bg-paper border-ink/12"}`}>
                <div className={`text-[0.6rem] font-bold uppercase tracking-[0.14em] mb-1 ${now ? "text-sage" : "text-coral"}`}>{when}</div>
                <div className={`font-disp font-bold text-lg mb-2 ${now ? "text-cream" : "text-ink"}`}>{name}</div>
                <p className={`text-sm leading-relaxed ${now ? "text-cream/75" : "text-mutedink"}`}>{body}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Closing */}
        <div className="px-6 md:px-10 py-8 bg-peach text-center">
          <div className="font-disp font-bold text-lg text-ink">Your skin · your record · your call.</div>
        </div>
      </div>
    </div>
  );
}
